import '../missive.js'

import renderAsHTML from 'preact-render-to-string'
import prettierConfig from '../../prettier.config.js'
import { HighLight, THEME } from 'preact-highlight'

import { render, Component } from 'preact'
import { AsyncButton, CollapsableBox, SVG, Tabs } from '../components'

const PAGES = [
  'Typography',
  'Layout',
  'Buttons',
  'Components',
  'Form Elements',
  'Icons',
  'CSS Variables',
  'CSS Animations',
]

class Styleguide extends Component {
  constructor() {
    super()

    let page = this.getPageFromHash()
    let theme = Missive.storeSync('theme') || 'light'

    this.state = { page, theme }

    for (let page of PAGES) {
      let renderFn = `render${page.replace(/\s/g, '')}`
      if (!this[renderFn]) continue

      this[renderFn] = this[renderFn].bind(this)
    }
  }

  componentWillMount() {
    for (let icon in SVGS) {
      Missive.injectSVGIcon(icon, SVGS[icon])
    }
  }

  componentDidMount() {
    window.onhashchange = () => {
      let page = this.getPageFromHash()
      this.setState({ page })
    }
  }

  getPageFromHash() {
    let { hash } = window.location

    if (!hash) {
      return PAGES[0]
    }

    return hash.replace(/^#/, '').replace(/_/g, ' ')
  }

  navigate(page) {
    let hash = `#${page.replace(/\s/g, '_')}`
    window.history.pushState(null, null, hash)

    this.setState({ page })
  }

  setTheme(theme) {
    this.setState({ theme })

    if (this.state.page == 'CSS Variables') {
      setTimeout(() => {
        this.setState({ theme })
      })
    }

    Missive.storeSync('theme', theme)
  }

  renderMissiveLogo() {
    return (
      <div class="columns-center">
        <a href="https://missiveapp.com" style={{ color: '#fff' }}>
          <div class="columns-center">
            <SVG name="missive-logo" size={38} />
          </div>

          <i class="icon icon-missive-logotype" style={{ width: 99, height: 19 }}>
            <svg style={{ width: 99, height: 19 }}>
              <use xlinkHref="#missive-logotype" />
            </svg>
          </i>
        </a>
      </div>
    )
  }

  renderNav() {
    return (
      <nav class="text-large padding columns-vertical columns-justify">
        <div>
          <div class="padding-bottom-large">{this.renderMissiveLogo()}</div>

          <ul>
            {PAGES.map((page) => {
              return (
                <li
                  onClick={() => this.navigate(page)}
                  class={Missive.classNames({
                    current: page == this.state.page,
                  })}
                >
                  {page}
                </li>
              )
            })}
          </ul>
        </div>

        <div class="columns-middle columns-justify margin-top-large">
          <span onClick={() => this.setTheme('light')}>
            <SVG name="sun" />
          </span>

          <input
            type="checkbox"
            class="switch"
            checked={this.state.theme == 'dark'}
            onClick={(e) => {
              let theme = this.state.theme == 'dark' ? 'light' : 'dark'
              this.setTheme(theme)
            }}
          />

          <span onClick={() => this.setTheme('dark')}>
            <SVG name="moon" />
          </span>
        </div>
      </nav>
    )
  }

  renderMain() {
    return (
      <main class="padding column-grow">
        <h1 class="padding-bottom-small">{this.state.page}</h1>
        <div class="padding-top padding-bottom" data-theme={this.state.theme}>
          {this.renderPageContent()}
        </div>
      </main>
    )
  }

  renderPageContent() {
    let { page } = this.state
    if (!page) return

    let renderFn = `render${page.replace(/\s/g, '')}`
    let fn = this[renderFn]
    if (!fn) return

    return fn()
  }

  renderExamples(prefix, values, content, applyToParent) {
    return (
      <table class="examples">
        <tbody>{values.map((v) => this.renderExample(prefix, v, content, applyToParent))}</tbody>
      </table>
    )
  }

  renderExample(prefix, value, content = 'Content', applyToParent = true) {
    let className = prefix ? `${prefix}-${value}` : value

    return (
      <tr>
        <td>
          <code class="code">.{className}</code>
        </td>
        <td>
          <div class={`box padding-small example ${applyToParent ? className : ''}`}>{content}</div>
        </td>
      </tr>
    )
  }

  renderTypography() {
    return (
      <div>
        <div class="box">
          <div class="box-header">Text size</div>
          <div class="box-content">
            {this.renderExamples('text', ['xsmall', 'small', 'normal', 'large', 'xlarge'])}
          </div>
        </div>

        <div class="box">
          <div class="box-header">Text color</div>
          <div class="box-content">{this.renderExamples('text', ['a', 'b', 'c', 'd', 'e'])}</div>
        </div>

        <div class="box">
          <div class="box-header">Text weight</div>
          <div class="box-content">{this.renderExamples('text', [400, 500, 600, 700, 800])}</div>
        </div>

        <div class="box">
          <div class="box-header">Text alignment</div>
          <div class="box-content">{this.renderExamples('align', ['left', 'center', 'right'])}</div>
        </div>

        <div class="box">
          <div class="box-header">Helpers</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                {this.renderExample('text', 'label', 'Label')}
                {this.renderExample(
                  null,
                  'ellipsis',
                  'Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                )}
                <tr>
                  <td>
                    <code class="code">.code</code>
                  </td>
                  <td>
                    <div class="code" style="display: inline-block">{`{ foo: 'bar' }`}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  renderLayout() {
    return (
      <div>
        <div class="box">
          <div class="box-header">Columns (Flexbox)</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                {this.renderExample(
                  null,
                  'columns',
                  <div class="columns">
                    <div class="box padding-small">Content</div>
                    <div class="box padding-small">
                      Content
                      <br />
                      Content
                    </div>
                    <div class="box padding-small">Content</div>
                  </div>,
                )}
                {this.renderExample(
                  null,
                  'columns-middle',
                  <div class="columns-middle">
                    <div class="box padding-small">Content</div>
                    <div class="box padding-small">
                      Content
                      <br />
                      Content
                    </div>
                    <div class="box padding-small">Content</div>
                  </div>,
                )}
                {this.renderExample(
                  null,
                  'columns-vertical',
                  <div class="columns-vertical">
                    <div class="box padding-small">Content</div>
                    <div class="box padding-small">
                      Content
                      <br />
                      Content
                    </div>
                    <div class="box padding-small">Content</div>
                  </div>,
                )}
                {this.renderExample(
                  null,
                  'columns-left',
                  <div class="columns">
                    <div class="box padding-small">Content</div>
                    <div class="box padding-small">
                      Content
                      <br />
                      Content
                    </div>
                    <div class="box padding-small">Content</div>
                  </div>,
                )}
                {this.renderExample(
                  null,
                  'columns-center',
                  <div class="columns">
                    <div class="box padding-small">Content</div>
                    <div class="box padding-small">
                      Content
                      <br />
                      Content
                    </div>
                    <div class="box padding-small">Content</div>
                  </div>,
                )}
                {this.renderExample(
                  null,
                  'columns-right',
                  <div class="columns">
                    <div class="box padding-small">Content</div>
                    <div class="box padding-small">
                      Content
                      <br />
                      Content
                    </div>
                    <div class="box padding-small">Content</div>
                  </div>,
                )}
                {this.renderExample(
                  null,
                  'columns-justify',
                  <div class="columns-justify">
                    <div class="box padding-small">Content</div>
                    <div class="box padding-small">
                      Content
                      <br />
                      Content
                    </div>
                    <div class="box padding-small">Content</div>
                  </div>,
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div class="box">
          <div class="box-header">Column (Flexbox)</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                {this.renderExample(
                  null,
                  'column-grow',
                  <div class="columns">
                    <div class="box padding-small">Content</div>
                    <div class="box padding-small column-grow">
                      Content
                      <br />
                      Content
                    </div>
                    <div class="box padding-small">Content</div>
                  </div>,
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div class="box">
          <div class="box-header columns-middle">
            <span>Margin</span>
            <div class="margin-left-small">
              {['All', 'Top', 'Right', 'Bottom', 'Left'].map((side) => {
                return (
                  <span
                    onClick={() => {
                      this.setState({ margin: side == 'All' ? null : side })
                    }}
                    class={Missive.classNames({
                      'button-token': true,
                      'button-token--selected': side == (this.state.margin || 'All'),
                    })}
                  >
                    {side}
                  </span>
                )
              })}
            </div>
          </div>
          <div class="box-content">
            <table class="examples margin">
              <tbody>
                {['xsmall', 'small', 'medium', '', 'large', 'xlarge'].map((size) => {
                  let prefix = this.state.margin
                    ? `margin-${this.state.margin.toLowerCase()}`
                    : 'margin'
                  let className = size ? `${prefix}-${size}` : prefix

                  return this.renderExample(
                    null,
                    className,
                    <div class="box margin-color">
                      <div class={`box padding-small ${className}`}>Content</div>
                    </div>,
                    false,
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div class="box">
          <div class="box-header columns-middle">
            <span>Padding</span>
            <div class="margin-left-small">
              {['All', 'Top', 'Right', 'Bottom', 'Left'].map((side) => {
                return (
                  <span
                    onClick={() => {
                      this.setState({ padding: side == 'All' ? null : side })
                    }}
                    class={Missive.classNames({
                      'button-token': true,
                      'button-token--selected': side == (this.state.padding || 'All'),
                    })}
                  >
                    {side}
                  </span>
                )
              })}
            </div>
          </div>
          <div class="box-content">
            <table class="examples padding">
              <tbody>
                {['xsmall', 'small', 'medium', '', 'large', 'xlarge'].map((size) => {
                  let prefix = this.state.padding
                    ? `padding-${this.state.padding.toLowerCase()}`
                    : 'padding'
                  let className = size ? `${prefix}-${size}` : prefix

                  return this.renderExample(
                    null,
                    className,
                    <div class={`box ${className} padding-color`}>
                      <div class="padding-xsmall">Content</div>
                    </div>,
                    false,
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div class="box">
          <div class="box-header">Helpers</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                {this.renderExample(
                  null,
                  'row + .row',
                  <div>
                    <div class="row">Content</div>
                    <div class="row">Content</div>
                  </div>,
                  false,
                )}
                {this.renderExample(
                  null,
                  'row-small + .row-small',
                  <div>
                    <div class="row-small">Content</div>
                    <div class="row-small">Content</div>
                  </div>,
                  false,
                )}
                {this.renderExample(
                  null,
                  'section + .section',
                  <div>
                    <div class="section">Content</div>
                    <div class="section">Content</div>
                  </div>,
                  false,
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  renderButtons() {
    return (
      <div>
        <div class="box">
          <div class="box-header">Standard</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                <tr>
                  <td>
                    <code class="code">.button</code>
                  </td>
                  <td>
                    <span class="button">Button</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <code class="code">.button-token</code>
                  </td>
                  <td>
                    <span class="button-token">Button</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <code class="code">.button-secondary</code>
                  </td>
                  <td>
                    <span class="button-secondary">Button</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <code class="code">.button-link</code>
                  </td>
                  <td>
                    <span class="button-link">Button</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="box">
          <div class="box-header">Group</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                <tr>
                  <td>
                    <code class="code">.button-group > .button</code>
                  </td>
                  <td>
                    <span class="button-group columns-middle">
                      <span class="button">Button</span>
                      <span class="separator"></span>
                      <span class="button button-more">
                        <SVG name="chevron-down" size={18} />
                      </span>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <code class="code">.button-group > .button.button-small</code>
                  </td>
                  <td>
                    <div class="columns">
                      <span class="button-group columns-middle">
                        <span class="button button-small">Button</span>
                        <span class="separator"></span>
                        <span class="button button-small button-more">
                          <SVG name="chevron-down" size={14} />
                        </span>
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="box">
          <div class="box-header">Links</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                <tr>
                  <td>
                    <code class="code">.button-link</code>
                  </td>
                  <td>
                    <span class="button-link">Button</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  renderComponents() {
    let tabs = ['One', 'Dos', 'Trois']

    let components = {
      asyncButton: (
        <AsyncButton
          label="Button"
          loading={this.state.asyncButtonLoading}
          alwaysRenderIcon={true}
          onClick={() => {
            if (this.state.asyncButtonLoading) return
            this.setState({ asyncButtonLoading: true })

            setTimeout(() => {
              this.setState({ asyncButtonLoading: false })
            }, 1250)
          }}
        />
      ),

      collapsableBox: (
        <CollapsableBox
          label="Header"
          opened={this.state.collapsableBoxOpened}
          onToggle={(opened) => this.setState({ collapsableBoxOpened: opened })}
          alwaysRenderContent={true}
          noAutoScroll={true}
          getContent={() => <div>Content</div>}
        />
      ),

      list: (
        <div>
          <div class="list-title columns-middle">
            <span class="list-title-icon" style="background-color: rgb(45, 171, 232);" />
            List name
          </div>
          <div class="list-items light-box">
            <div class="list-item padding-small">
              <div class="columns-middle">
                <div class="margin-right-small">
                  <input type="checkbox" class="checkmark" />
                </div>
                <div class="list-item-title list-item-meta column-grow ellipsis">
                  <span>Item 1</span>
                </div>
                <time
                  class="list-item-due-at list-item-due-at--overdue list-item-meta"
                  datetime="1/21/2019"
                >
                  Jan 21
                </time>
                <div class="list-item-meta">
                  <span
                    class="list-item-tag-icon stack-item"
                    style="z-index: 1; background-color: rgb(69, 136, 221);"
                  />
                  <span
                    class="list-item-tag-icon stack-item"
                    style="z-index: 1; background-color: rgb(251, 153, 39);"
                  />
                </div>
                <div class="avatar list-item-meta" style="width: 18px;" />
              </div>
            </div>
            <div class="list-item padding-small">
              <div class="columns-middle">
                <div class="margin-right-small">
                  <input type="checkbox" class="checkmark" />
                </div>
                <div class="list-item-title list-item-meta column-grow ellipsis">
                  <span>Item 2</span>
                </div>
                <time class="list-item-due-at list-item-meta" datetime="2/25/2019, 1:00:00 PM">
                  Today&nbsp;at 13:00
                </time>
                <div class="list-item-meta">
                  <span
                    class="list-item-tag-icon stack-item"
                    style="z-index: 1; background-color: rgb(251, 153, 39);"
                  />
                </div>
                <div class="avatar list-item-meta" style="width: 18px;">
                  <img
                    src="https://gravatar.com/avatar/014bcc1f9c5920c658a198e6649cd20b"
                    style="width: 18px;"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ),

      tabs: (
        <Tabs
          selectedIndex={this.state.tabsSelectedIndex}
          onTabSelected={(index) => {
            this.setState({ tabsSelectedIndex: index })
          }}
          getContentForIndex={(i) => {
            return <div>{tabs[i]}</div>
          }}
          tabs={tabs.map((label) => {
            return { label }
          })}
        />
      ),
    }

    return (
      <div>
        <div class="box">
          <div class="box-header">Async Button</div>
          <div class="box-content columns-middle">
            {this.renderRaw({ component: components.asyncButton })}
            <div class="column-grow columns-center">{components.asyncButton}</div>
          </div>
        </div>

        <div class="box">
          <div class="box-header">Collapsable Box</div>
          <div class="box-content columns">
            {this.renderRaw({ component: components.collapsableBox })}
            <div class="column-grow padding-left-xlarge padding-right-xlarge">
              {components.collapsableBox}
            </div>
          </div>
        </div>

        <div class="box">
          <div class="box-header">List</div>
          <div class="box-content columns-middle">
            {this.renderRaw({ component: components.list })}
            <div class="column-grow">{components.list}</div>
          </div>
        </div>

        <div class="box">
          <div class="box-header">Tabs</div>
          <div class="box-content columns tabs-example">
            {this.renderRaw({ component: components.tabs })}
            <div class="column-grow padding-left-xlarge">{components.tabs}</div>
          </div>
        </div>
      </div>
    )
  }

  renderFormElements() {
    return (
      <div>
        <div class="box">
          <div class="box-header">
            <code class="code">input[type="checkbox"]</code>
          </div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                <tr>
                  <td>
                    <code class="code">.checkmark</code>
                  </td>
                  <td>
                    <input type="checkbox" class="checkmark" />
                  </td>
                </tr>
                <tr>
                  <td>
                    <code class="code">.checkmark-large</code>
                  </td>
                  <td>
                    <input type="checkbox" class="checkmark-large" />
                  </td>
                </tr>
                <tr>
                  <td>
                    <code class="code">.switch</code>
                  </td>
                  <td>
                    <input type="checkbox" class="switch" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }
  renderIcons() {
    return (
      <div>
        <div class="box">
          <div class="box-header">SVG (Usage)</div>
          <div class="box-content">
            {this.renderRaw({
              html: `<svg style="width: 24px; height: 24px"><use xlink:href="#ICON_ID" /></svg>`,
            })}
          </div>
        </div>

        <div class="box">
          <div class="box-header">Icon IDs</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                {Object.keys(Missive.SVGS).map((icon) => {
                  return (
                    <tr>
                      <td>
                        <code class="code">{icon}</code>
                      </td>
                      <td>
                        <SVG name={icon} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div class="box">
          <div class="box-header">Helpers</div>
          <div class="box-content">
            <table class="examples">
              <tbody>
                <tr>
                  <td>
                    <code class="code">[data-badge=2]</code>
                  </td>
                  <td>
                    <SVG name="comment" badge={2} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  renderCSSVariables() {
    let elem = document.querySelector('[data-theme]')

    if (!elem) {
      return requestAnimationFrame(() => {
        this.forceUpdate()
      })
    }

    let computedStyle = getComputedStyle(
      document.querySelector('[data-theme]') || document.documentElement,
    )

    return (
      <div>
        <div class="box">
          <div class="box-header">Defaults ({this.state.theme} theme)</div>
          <div class="box-content">
            {this.renderRaw({
              skipFormat: true,
              language: 'less',
              html: CSS_VARS.map((varName) => {
                if (!varName) return ''

                let varValue = computedStyle.getPropertyValue(varName).trim()

                return `${varName}: ${varValue}`
              }).join('\n'),
            })}
          </div>
        </div>
      </div>
    )
  }

  renderCSSAnimations() {
    return (
      <div>
        <div class="box">
          <div class="box-content">
            <table class="examples animation">
              <tbody>
                <tr>
                  <td>
                    <code class="code">rotate</code>
                  </td>
                  <td>
                    <div class="rotate">
                      <SVG name="account-circle" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  renderRaw({ component, html, skipFormat, language } = {}) {
    html || (html = renderAsHTML(component))
    language || (language = 'html')

    if (!skipFormat) {
      html = prettier.format(html, PRETTIER_OPTIONS).replace(/^;/, '')
    }

    return (
      <HighLight
        className=""
        language={language}
        theme={this.state.theme == 'dark' ? THEME.tomorrowNight : THEME.github}
        code={html}
      />
    )

    return (
      <pre>
        <code>{formatted}</code>
      </pre>
    )
  }

  render() {
    return (
      <div class="columns column-grow">
        {this.renderNav()}
        {this.renderMain()}
      </div>
    )
  }
}

const SVGS = {
  'sun': {
    viewBox: '0 0 24 24',
    svg:
      '<path d="M3.55,18.54L4.96,19.95L6.76,18.16L5.34,16.74M11,22.45C11.32,22.45 13,22.45 13,22.45V19.5H11M12,5.5A6,6 0 0,0 6,11.5A6,6 0 0,0 12,17.5A6,6 0 0,0 18,11.5C18,8.18 15.31,5.5 12,5.5M20,12.5H23V10.5H20M17.24,18.16L19.04,19.95L20.45,18.54L18.66,16.74M20.45,4.46L19.04,3.05L17.24,4.84L18.66,6.26M13,0.55H11V3.5H13M4,10.5H1V12.5H4M6.76,4.84L4.96,3.05L3.55,4.46L5.34,6.26L6.76,4.84Z" />',
  },
  'moon': {
    viewBox: '0 0 24 24',
    svg:
      '<path d="M17.75,4.09L15.22,6.03L16.13,9.09L13.5,7.28L10.87,9.09L11.78,6.03L9.25,4.09L12.44,4L13.5,1L14.56,4L17.75,4.09M21.25,11L19.61,12.25L20.2,14.23L18.5,13.06L16.8,14.23L17.39,12.25L15.75,11L17.81,10.95L18.5,9L19.19,10.95L21.25,11M18.97,15.95C19.8,15.87 20.69,17.05 20.16,17.8C19.84,18.25 19.5,18.67 19.08,19.07C15.17,23 8.84,23 4.94,19.07C1.03,15.17 1.03,8.83 4.94,4.93C5.34,4.53 5.76,4.17 6.21,3.85C6.96,3.32 8.14,4.21 8.06,5.04C7.79,7.9 8.75,10.87 10.95,13.06C13.14,15.26 16.1,16.22 18.97,15.95M17.33,17.97C14.5,17.81 11.7,16.64 9.53,14.5C7.36,12.31 6.2,9.5 6.04,6.68C3.23,9.82 3.34,14.64 6.35,17.66C9.37,20.67 14.19,20.78 17.33,17.97Z" />',
  },
  'missive-logo': {
    viewBox: '0 0 17 17',
    svg:
      '<path d="M16.7403212,12.0024915 L15.9800467,12.0024915 C15.7586057,12.0024915 15.5469675,11.9085701 15.399405,11.7448197 L13.2593924,9.37063121 L12.5626093,10.1437112 L14.6251079,12.4319017 C14.9695718,12.814029 15.4634158,13.0331789 15.9800467,13.0331789 L16.7403212,13.0331789 C16.8837938,13.0331789 17,12.9178064 17,12.7755071 L17,12.2601634 C17,12.1178641 16.8837938,12.0024915 16.7403212,12.0024915 M3.74060765,9.37063121 L1.60059497,11.7448197 C1.45296759,11.9085701 1.24139432,12.0024915 1.01995326,12.0024915 L0.259678762,12.0024915 C0.116206246,12.0024915 0,12.1178641 0,12.2601634 L0,12.7755071 C0,12.9178064 0.116206246,13.0331789 0.259678762,13.0331789 L1.01995326,13.0331789 C1.53664907,13.0331789 2.03049316,12.814029 2.3748272,12.4319017 L4.43739069,10.1436468 L3.74060765,9.37063121 Z M8.5,12.0696794 C8.05718279,12.0696794 7.63384149,11.8818367 7.33865166,11.5543357 L1.60059497,5.18835936 C1.45296759,5.0246089 1.24139432,4.93068751 1.01995326,4.93068751 L0.259678762,4.93068751 C0.116271166,4.93068751 0,4.81531494 0,4.67301565 L0,4.15767195 C0,4.01537267 0.116271166,3.9000001 0.259678762,3.9000001 L1.01995326,3.9000001 C1.53664907,3.9000001 2.03049316,4.11915001 2.3748272,4.50127736 L8.10178262,10.8548855 C8.19792868,10.9615616 8.33166324,11.0322281 8.47578496,11.0384767 C8.63490312,11.0454338 8.78194622,10.9839147 8.88711611,10.8671893 L14.6251728,4.50127736 C14.9695718,4.11915001 15.4634158,3.9000001 15.9800467,3.9000001 L16.7403212,3.9000001 C16.8837938,3.9000001 17,4.01537267 17,4.15767195 L17,4.67301565 C17,4.81531494 16.8837938,4.93068751 16.7403212,4.93068751 L15.9800467,4.93068751 C15.7586057,4.93068751 15.5469675,5.0246089 15.399405,5.18835936 L9.66134834,11.5543357 C9.36615851,11.8818367 8.94288213,12.0696794 8.5,12.0696794 Z"></path>',
  },
  'missive-logotype': {
    viewBox: '0 0 100 19',
    svg:
      '<path d="M84.7027007,8.45808336 C84.9377737,5.67645836 86.8627372,3.61414586 89.6794526,3.61414586 C92.2617883,3.61414586 94.2807117,5.31697919 94.4686314,8.45808336 L84.7027007,8.45808336 L84.7027007,8.45808336 Z M96.6987044,10.4003334 C96.7455109,10.2328125 96.7690876,9.82516669 96.7690876,9.48941669 C96.7690876,4.98122919 94.1631752,1.60000002 89.7030292,1.60000002 C85.2193066,1.60000002 82.355438,5.02904169 82.355438,9.89706252 C82.355438,14.836625 85.2896898,18.1459584 89.9145255,18.1459584 C92.5904745,18.1459584 94.6093978,17.0668125 96.0649088,15.1964584 L94.3982482,13.8297292 C93.3650365,15.1482917 91.9566788,16.0599167 89.9613321,16.0599167 C86.7687774,16.0599167 84.7734307,13.73375 84.7027007,10.4003334 L96.6987044,10.4003334 L96.6987044,10.4003334 Z M78.857427,1.88758336 L73.9982117,15.2205417 L69.2093796,1.88758336 L66.8381934,1.88758336 L72.8478102,17.858375 L75.1250365,17.858375 L81.1346533,1.88758336 L78.857427,1.88758336 L78.857427,1.88758336 Z M62.2605109,17.858375 L64.4437774,17.858375 L64.4437774,1.88793752 L62.2605109,1.88793752 L62.2605109,17.858375 Z M52.9411496,1.60000002 C49.7954015,1.60000002 47.4713686,3.25466669 47.4713686,6.13227086 C47.4713686,9.15366669 49.9829745,10.0649375 52.6356934,10.6401042 C55.0536861,11.1918959 56.8378832,11.5517292 56.8378832,13.4458125 C56.8378832,15.172375 55.4059489,16.179625 53.2930657,16.179625 C51.4384854,16.179625 49.654635,15.4362292 48.5746168,13.6855834 L46.7671898,14.8607084 C47.9408212,16.851125 50.1708942,18.1459584 53.1287226,18.1459584 C56.7439234,18.1459584 59.0679562,16.179625 59.0679562,13.2542084 C59.0679562,10.0408542 56.2980474,9.20147919 53.2930657,8.48216669 C51.297719,8.00262502 49.677865,7.61906252 49.677865,5.91622919 C49.677865,4.21375002 51.1805292,3.59041669 52.8236131,3.59041669 C54.6078102,3.59041669 56.1337044,4.30972919 57.1194161,5.65237502 L58.8096533,4.42979169 C57.4481022,2.58316669 55.3119891,1.60000002 52.9411496,1.60000002 L52.9411496,1.60000002 Z M38.7386496,1.60000002 C35.5929015,1.60000002 33.2688686,3.25466669 33.2688686,6.13227086 C33.2688686,9.15366669 35.7808212,10.0649375 38.4335401,10.6401042 C40.8511861,11.1918959 42.6353832,11.5517292 42.6353832,13.4458125 C42.6353832,15.172375 41.2034489,16.179625 39.0905657,16.179625 C37.2363321,16.179625 35.452135,15.4362292 34.3721168,13.6855834 L32.5646898,14.8607084 C33.7383212,16.851125 35.9683942,18.1459584 38.9262226,18.1459584 C42.5414234,18.1459584 44.8654562,16.179625 44.8654562,13.2542084 C44.8654562,10.0408542 42.0955474,9.20147919 39.0905657,8.48216669 C37.095219,8.00262502 35.4757117,7.61906252 35.4757117,5.91622919 C35.4757117,4.21375002 36.9780292,3.59041669 38.6211131,3.59041669 C40.4053102,3.59041669 41.9312044,4.30972919 42.9172628,5.65237502 L44.6075,4.42979169 C43.2459489,2.58316669 41.1094891,1.60000002 38.7386496,1.60000002 L38.7386496,1.60000002 Z M27.4471715,17.858375 L29.630438,17.858375 L29.630438,1.88793752 L27.4471715,1.88793752 L27.4471715,17.858375 Z M13.7845073,8.98579169 C13.7845073,5.65237502 15.4747445,3.71012502 17.9395438,3.71012502 C20.3811131,3.71012502 21.2024818,5.50858336 21.2024818,7.54681252 L21.2024818,17.858375 L23.3857482,17.858375 L23.3857482,7.09135419 C23.3857482,3.97397919 21.672281,1.60000002 18.3857664,1.60000002 C16.1085401,1.60000002 14.4654562,2.75104169 13.3386314,4.62139586 C12.6341058,2.75104169 11.0145985,1.60000002 8.87813869,1.60000002 C6.95317518,1.60000002 5.38047445,2.53535419 4.20649635,4.28564586 L4.20649635,1.88758336 L2,1.88758336 L2,17.858375 L4.20649635,17.858375 L4.20649635,8.98579169 C4.20649635,5.65237502 5.87350365,3.71012502 8.33830292,3.71012502 C10.7798723,3.71012502 11.6012409,5.50858336 11.6012409,7.54681252 L11.6012409,17.858375 L13.7845073,17.858375 L13.7845073,8.98579169 L13.7845073,8.98579169 Z"></path>',
  },
}

const CSS_VARS = [
  '--animation-duration',
  '--animation-easing',
  '--checkmark-color',
  '--accent-color',
  '',
  '--missive-font-size',
  '--missive-border-radius',
  '',
  '--missive-text-color-a',
  '--missive-text-color-b',
  '--missive-text-color-c',
  '--missive-text-color-d',
  '--missive-text-color-e',
  '',
  '--missive-blue-color',
  '--missive-orange-color',
  '--missive-green-color',
  '--missive-red-color',
  '--missive-yellow-color',
  '--missive-light-blue-color',
  '--missive-light-orange-color',
  '',
  '--missive-border-color',
  '--missive-light-border-color',
  '',
  '--missive-conversation-list-border-color',
  '--missive-conversation-list-background-color',
  '--missive-conversations-separator-opacity',
  '',
  '--missive-background-color',
  '--missive-light-active-color',
  '--missive-token-background-color',
  '--missive-section-shadow-width',
  '--missive-section-shadow-color',
]

const PRETTIER_OPTIONS = Object.assign({}, prettierConfig, {
  parser: 'babylon',
  plugins: prettierPlugins,
})

render(<Styleguide />, document.querySelector('#component'))
